// ---------------------------------------------------------------------------
// PWA_ControlBillet — Config screen (accessible via bottom nav)
// Allows editing configuration while the app is running.
// ---------------------------------------------------------------------------

import { useState, useEffect } from 'react';
import { useConfigStore } from '@/store/config-store';
import { useAppStore } from '@/store/app-store';
import { Toggle } from '@/components/Toggle';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ConfigScreen() {
  const config = useConfigStore();
  const showToast = useAppStore((s) => s.showToast);

  // Form state — loaded from store on mount.
  const [eventId, setEventId] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [vigileId, setVigileId] = useState('');
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [vibrationEnabled, setVibrationEnabled] = useState(true);
  const [autoSync, setAutoSync] = useState(true);

  // Load existing config on mount.
  useEffect(() => {
    const s = useConfigStore.getState();
    setEventId(s.eventId);
    setEventDate(s.eventDate);
    setVigileId(s.vigileId);
    setSoundEnabled(s.soundEnabled);
    setVibrationEnabled(s.vibrationEnabled);
    setAutoSync(s.autoSync);
  }, []);

  // Save changes.
  function handleSave() {
    if (!eventId || !eventDate) {
      showToast('Veuillez remplir les champs obligatoires', 'warning');
      return;
    }

    config.setConfig({
      eventId,
      eventDate,
      vigileId,
      soundEnabled,
      vibrationEnabled,
      autoSync,
    });

    showToast('Configuration mise a jour', 'success');
  }

  // Reset config.
  function handleReset() {
    if (window.confirm('Reinitialiser toute la configuration ?')) {
      config.reset();
      showToast('Configuration reinitialisee', 'warning');
    }
  }

  return (
    <div className="h-full overflow-y-auto pt-14 pb-20 px-4 py-4">
      <div className="bg-surface-card rounded-2xl p-5 space-y-5 shadow-xl">
        {/* Title */}
        <div>
          <h2 className="text-lg font-bold text-white">Configuration</h2>
          <p className="text-gray-500 text-xs mt-0.5">
            Appareil: {config.deviceId}
          </p>
        </div>

        {/* Event ID */}
        <div className="space-y-1">
          <label className="text-sm text-gray-400">
            ID Evenement <span className="text-danger">*</span>
          </label>
          <input
            type="number"
            value={eventId}
            onChange={(e) => setEventId(e.target.value)}
            placeholder="12345"
            className="w-full bg-surface border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-accent text-sm"
          />
        </div>

        {/* Event Date */}
        <div className="space-y-1">
          <label className="text-sm text-gray-400">
            Date de l&apos;evenement <span className="text-danger">*</span>
          </label>
          <input
            type="date"
            value={eventDate}
            onChange={(e) => setEventDate(e.target.value)}
            className="w-full bg-surface border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-accent text-sm [color-scheme:dark]"
          />
        </div>

        {/* Vigile Name */}
        <div className="space-y-1">
          <label className="text-sm text-gray-400">Nom du vigile</label>
          <input
            type="text"
            value={vigileId}
            onChange={(e) => setVigileId(e.target.value)}
            placeholder="Jean Dupont"
            className="w-full bg-surface border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-accent text-sm"
          />
        </div>

        {/* Preferences */}
        <div className="border-t border-white/10 pt-4">
          <p className="text-xs text-gray-500 font-medium mb-3 uppercase tracking-wider">
            Preferences
          </p>
          <div className="space-y-2">
            <Toggle
              label="Sons actives"
              enabled={soundEnabled}
              onChange={setSoundEnabled}
            />
            <Toggle
              label="Vibrations activees"
              enabled={vibrationEnabled}
              onChange={setVibrationEnabled}
            />
            <Toggle
              label="Sync automatique"
              enabled={autoSync}
              onChange={setAutoSync}
            />
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleSave}
            className="flex-1 btn-primary text-sm"
          >
            Sauvegarder
          </button>
        </div>

        {/* Reset button */}
        <button
          type="button"
          onClick={handleReset}
          className="w-full text-center text-danger text-xs font-medium py-2 active:opacity-60 transition-opacity"
        >
          Reinitialiser la configuration
        </button>
      </div>
    </div>
  );
}
